import { KeyboardEvent, useEffect, useRef, useState } from "react"
import { throttle } from "@martinstark/throttle-ts";
import { RenderInput, Resolution, Unit } from "../types"
import { PluginFactory } from "./types"
import { soundboard } from "../.."

type PluginProps = {
    always?: boolean
}

const [throttleErrorSound] = throttle(() => soundboard.error(), 100)
const applyScrollbar = (row: Unit[], rowPosition: number, scrollOffset: number, length: number, resolution: Resolution) => {
    let returnRow = [...row]
    if (returnRow.length < resolution.width) {
        returnRow = [...returnRow, ...Array(resolution.width - returnRow.length).fill(' ')]
    }

    const maxScroll = length - resolution.height
    const relativeScrollOffset = scrollOffset > maxScroll ? maxScroll : scrollOffset
    const relativeRowPosition = rowPosition - relativeScrollOffset

    let scrollbarPosition = Math.floor((relativeScrollOffset / (maxScroll - 1)) * (resolution.height) + 1)
    if (scrollbarPosition > resolution.height - 1) scrollbarPosition = resolution.height - 1
    if (relativeScrollOffset > 0 && scrollbarPosition === 0) scrollbarPosition = 1


    switch (relativeRowPosition) {
        case scrollbarPosition:
            returnRow[resolution.width] = '█'
            break
        case 0:
            returnRow[resolution.width] = scrollOffset > 0 ? '▲' : '▄'
            break
        case resolution.height:
            returnRow[resolution.width] = scrollOffset < maxScroll ? '▼' : '█'
            break
        default:
            returnRow[resolution.width] = '░'
            break
    }


    return returnRow
}
const useScrollable: PluginFactory<PluginProps> = ({ always = true } = {}) => {
    const [scrollOffset, setScrollOffset] = useState<number>(0)
    const scrollOffsetRef = useRef<number>(scrollOffset)
    scrollOffsetRef.current = scrollOffset

    const [maxScrollOffset, setMaxScrollOffset] = useState<number>(1)
    const maxScrollOffsetRef = useRef<number>(maxScrollOffset)
    maxScrollOffsetRef.current = maxScrollOffset

    useEffect(() => {
        const handleScroll = (event: WheelEvent) => {
            event.stopPropagation()
            let newPos = event.deltaY > 0 ? scrollOffsetRef.current + 1 :
                event.deltaY < 0 ? scrollOffsetRef.current - 1 :
                scrollOffsetRef.current

            if (newPos < 0) {
                throttleErrorSound()
                newPos = 0
            }
            if (maxScrollOffsetRef.current && newPos > maxScrollOffsetRef.current) {
                throttleErrorSound()
                newPos = maxScrollOffsetRef.current
            }

            setScrollOffset(newPos)

        }

        const handleKeyDown = (event: Event) => {
            const keyDownEvent = event as unknown as KeyboardEvent<Document>

            setTimeout(() => {
                if (keyDownEvent.key === "ArrowUp") {
                    setScrollOffset(current => {
                        if (current - 1 < 0) throttleErrorSound()
                        return current - 1 < 0 ? 0 : current - 1
                    })
                }

                if (keyDownEvent.key === "ArrowDown") {
                    setScrollOffset(current => {
                        if (current + 1 > maxScrollOffsetRef.current) throttleErrorSound()
                        return current + 1 > maxScrollOffsetRef.current ? maxScrollOffsetRef.current : current + 1
                    })
                }
            })

        }

        window.addEventListener('wheel', handleScroll)

        if (document.activeElement === document.body) {
            document.addEventListener('keydown', handleKeyDown)
        }
        return () => {
            window.removeEventListener('wheel', handleScroll)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [maxScrollOffset])


    return (input, tick, resolution) => {
        const maxScroll = input.length - resolution.height
        setMaxScrollOffset(maxScroll)

        let returnInput: RenderInput = []

        input.map((row: Unit[], y: number) => {
            const relativeScrollOffset = scrollOffsetRef.current > maxScroll ? maxScroll : scrollOffsetRef.current
            if (y < relativeScrollOffset) return
            if (y > resolution.height + relativeScrollOffset) return
            returnInput.push(applyScrollbar(
                row,
                y,
                scrollOffsetRef.current,
                input.length,
                resolution,
            ))
        })

        return [...returnInput]
    }
}

export default useScrollable
